import { ToastService } from 'src/app/services/toast.service';
import { inject } from '@angular/core';
import { Component, EventEmitter, Output, OnInit , ChangeDetectorRef } from '@angular/core';
import { WizardStateService } from '../../../services/wizard-state.service';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-upload-map-step',
    templateUrl: './upload-map-step.component.html',
    styleUrls: ['./upload-map-step.component.scss'],
    standalone: false
})
export class UploadMapStepComponent implements OnInit {
  private readonly toasts = inject(ToastService);
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  mapGenreList = [
    { label: 'Science Fiction & Science Fantasy (Traveller, etc)' },
    { label: 'Fantasy (Dungeons & Dragons, etc)' },
    { label: 'Modern & Horror (Call of Cthulhu, etc)' }
  ];

  mapFileName: string | null = null;
  jsonFileName: string | null = null;
  selectedGenre: string | null = null;
  mapName: string = '';
  mapNameConfirmed: boolean = false;
  isShowingHelp: boolean = false;

  private selectedMapFile: File | null = null;
  private selectedJsonFile: File | null = null;

  constructor(private stateService: WizardStateService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.stateService.state.pipe(take(1)).subscribe(state => {
      if (state.mapFile) {
        this.mapFileName = state.mapFile.name;
        this.selectedMapFile = state.mapFile;
      }
      if (state.jsonFile) {
        this.jsonFileName = state.jsonFile.name;
        this.selectedJsonFile = state.jsonFile;
      }
      if (state.mapJson) {
        if (!this.jsonFileName) {
          this.jsonFileName = state.mapName ? `${state.mapName}.json` : 'uploaded-map.json';
        }
      }
      if (state.mapName) {
        this.mapName = state.mapName;
        this.mapNameConfirmed = true;
      }
      if (state.mapGenre) {
        this.selectedGenre = state.mapGenre;
      }
      this.cdr.detectChanges();
    });
  }

  toggleHelp(): void {
    this.isShowingHelp = !this.isShowingHelp;
  }

  onMapFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.mapFileName = file.name;
      this.selectedMapFile = file;
      this.stateService.updateState({ mapFile: file });
    }
  }

  onJsonFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.jsonFileName = file.name;
      this.selectedJsonFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const parsed = JSON.parse(e.target.result);

          // QA: reject maps exceeding Azgaar's 100k cell limit
          const cellCount = parsed?.pack?.cells?.length ?? parsed?.grid?.cells?.length ?? 0;
          if (cellCount > 100000) {
            this.toasts.showNote('This map is too large. Maps can have up to 100,000 cells.', 'warning');
            this.jsonFileName = null;
            this.selectedJsonFile = null;
            return;
          }

          // Client-side extraction mirroring backend _parse_azgaar_prefill
          const pack = parsed.pack || {};
          const info = parsed.info || {};
          const options = parsed.settings?.options || {};

          const cultures = (pack.cultures || [])
            .filter((c: any) => typeof c === 'object' && c && (c.i > 0 || c.azgaar_id > 0))
            .map((c: any) => ({
              azgaarId: c.i || c.azgaar_id,
              name: c.name || '',
              type: c.type || '',
              temperament: '',
              expansionDrive: '',
              notes: ''
            }));

          const religions = (pack.religions || [])
            .filter((r: any) => typeof r === 'object' && r && (r.i > 0 || r.azgaar_id > 0))
            .map((r: any) => ({
              azgaarId: r.i || r.azgaar_id,
              name: r.name || '',
              type: r.type || '',
              form: r.form || '',
              deity: r.deity || '',
              zeal: '',
              convertPressure: '',
              notes: ''
            }));

          const states = (pack.states || [])
            .filter((s: any) => typeof s === 'object' && s && (s.i > 0 || s.azgaar_id > 0))
            .map((s: any) => ({
              azgaarId: s.i || s.azgaar_id,
              name: s.name || '',
              type: s.type || '',
              form: s.form || '',
              formName: s.formName || '',
              corruption: '',
              domesticPolicy: '',
              foreignPolicy: '',
              notes: ''
            }));

          const provinces = (pack.provinces || [])
            .filter((p: any) => typeof p === 'object' && p && (p.i > 0 || p.azgaar_id > 0))
            .map((p: any) => ({
              azgaarId: p.i || p.azgaar_id,
              name: p.name || '',
              fullName: p.fullName || '',
              formName: p.formName || '',
              state: p.state || 0,
              stability: '',
              prosperity: '',
              notes: ''
            }));

          // Calculate minimum required years client-side: clamp(40, (nC + nR + nS) * 5, 240) * 10
          const nC = cultures.length;
          const nR = religions.length;
          const nS = states.length;
          const minYearsRequired = Math.max(40, Math.min((nC + nR + nS) * 5, 240)) * 10;

          const currentEra = options.era ? {
            name: options.era,
            year: options.year || 0,
            short: options.eraShort || ''
          } : null;

          const detectedMapName = info.mapName || this.mapName;
          if (detectedMapName && !this.mapName) {
            this.mapName = detectedMapName;
          }

          this.stateService.updateState({
            mapJson: parsed,
            jsonFile: file,
            mapName: this.mapName || detectedMapName,
            cultures,
            religions,
            states,
            provinces,
            minYearsRequired,
            currentEra
          });
        } catch (err) {
          this.toasts.showNote("That .json file isn't a valid Azgaar map export.", 'danger');
          this.jsonFileName = null;
          this.selectedJsonFile = null;
        }
      
          this.cdr.detectChanges();};
      reader.readAsText(file);
    }
  }

  onNext(): void {
    const mapName = this.mapName.trim();

    if (!mapName || !this.selectedMapFile || !this.selectedJsonFile || !this.selectedGenre) {
      this.toasts.showNote('Add a map name, the .map file, the .json file and a genre to continue.', 'warning');
      return;
    }

    if (!this.mapNameConfirmed) {
      this.toasts.showNote('Confirm the map name to continue.', 'warning');
      return;
    }

    this.stateService.updateState({
      mapName,
      mapGenre: this.selectedGenre,
      mapFile: this.selectedMapFile,
      jsonFile: this.selectedJsonFile
    });

    this.next.emit();
  }

  onBack(): void {
    this.back.emit();
  }
}
